import { lazy } from "react";
import { SessionUser } from "../localstorage";

const separator = "\\";

/**
 * Converts file-system based pages into React Router compatible routes.
 *
 * @param {Record<string, () => Promise<unknown>>} files
 * @param {Record<string, () => Promise<unknown>>} loadingFiles
 * @returns {object} route config
 */
export function convertPagesToRoute(files, loadingFiles = {}) {
  let routes = { path: "/" };

  Object.entries(files).forEach(([filePath, importer]) => {
    const segments = getRouteSegmentsFromFilePath(filePath);
    const page = lazy(importer);

    const loadingComponent = findMatchingLoadingComponent(filePath, loadingFiles);

    const route = createRoute({
      PageComponent: page,
      LoadingComponent: loadingComponent,
      segments,
      async action(args) {
        const result = await importer();
        return "action" in result ? result.action?.(args) : null;
      },
      async loader(args) {
        const result = await importer();
        return "loader" in result ? result.loader?.(args) : null;
      },
      async guard() {
        const result = await importer();
        const userRole = SessionUser.get()?.user?.role; // Get role as string (e.g., "BAE", "Emiten")
        
        // If no permissions defined in route, allow access
        if (!("permissions" in result)) {
          return true;
        }

        // If permissions include "All", allow access for any logged-in user
        if (result.permissions?.includes("All")) {
          return true;
        }

        // Check if user's role is in the allowed permissions list
        const isAllowed = userRole && result.permissions?.includes(userRole);

        return isAllowed || false;
      },
    });

    routes = mergeRoutes(routes, route);
  });

  return routes;
}

/**
 * Determines the appropriate loading component for a route.
 */
function findMatchingLoadingComponent(filePath, loadingFiles) {
  const loadingPath = filePath.replace(/(page|layout)\.tsx$/, "loading.tsx");

  const groupMatch = filePath.match(/\([^/]+\//);
  const groupLoadingPath = groupMatch ? `/${groupMatch[0]}loading.tsx` : null;

  const globalLoadingPath = "./app/loading.tsx";

  const loader =
    loadingFiles[loadingPath] ||
    (groupLoadingPath && loadingFiles[groupLoadingPath]) ||
    loadingFiles[globalLoadingPath];

  if (!loader) return undefined;

  return lazy(loader);
}

/**
 * Merges two route configurations while maintaining proper hierarchy.
 */
export function mergeRoutes(target, source) {
  if (target.path !== source.path)
    throw new Error(`Paths do not match: "${target.path}" and "${source.path}"`);

  target.children = target.children || [];

  // Layout first
  if (source.handle?.pageType === "layout") {
    return handleLayoutMerge(target, source);
  }

  // Page
  if (source.handle?.pageType === "page") {
    return handlePageMerge(target, source);
  }

  // Nested routes
  if (source.children && source.children.length > 0) {
    if (target.handle?.pageType === "page") {
      if (!target.children?.some((child) => child.index)) {
        target.children = target.children || [];
        target.children.unshift({
          index: true,
          element: target.element,
          HydrateFallback: target.HydrateFallback,
          action: target.action,
          loader: target.loader,
          handle: target.handle,
          errorElement: target.errorElement,
        });
      }

      delete target.element;
      delete target.action;
      delete target.loader;
      delete target.handle;
    }

    mergeChildRoutes(target, source);
  }

  return target;
}

/**
 * Merges child routes from source to target.
 */
export function mergeChildRoutes(target, source) {
  if (!source.children) return;

  if (!target.children) {
    target.children = [];
  }

  source.children.forEach((sourceChild) => {
    const matchingChild = target.children.find(
      (targetChild) => targetChild.path === sourceChild.path,
    );

    if (matchingChild) {
      mergeRoutes(matchingChild, sourceChild);
    } else {
      target.children.push(sourceChild);
    }
  });
}

/**
 * Handles merging of a layout route.
 */
export function handleLayoutMerge(target, source) {
  if (!target.element) {
    Object.assign(target, {
      element: source.element,
      HydrateFallback: source.HydrateFallback,
      action: source.action,
      loader: source.loader,
      handle: source.handle,
      errorElement: source.errorElement,
    });
  } else if (target.handle?.pageType === "page") {
    target = swapTargetRouteAsIndexRouteAndUpdateWithRoute(target, source);
  }

  return target;
}

/**
 * Handles merging of a page route.
 */
export function handlePageMerge(target, source) {
  if (!target.children) {
    target.children = [];
  }

  if (!target.children.some((child) => child.index) || target.handle?.pageType === "layout") {
    if (target.handle?.pageType === "layout") {
      addRouteAsIndexRouteForTargetRoute(target, source);
    } else {
      target.children.unshift({
        index: true,
        element: source.element,
        HydrateFallback: source.HydrateFallback,
        action: source.action,
        loader: source.loader,
        handle: source.handle,
        errorElement: source.errorElement,
      });
    }
  }

  return target;
}

/**
 * Converts page route to index route under layout, then applies layout.
 */
export function swapTargetRouteAsIndexRouteAndUpdateWithRoute(target, layout) {
  target.children = target.children || [];
  target.children.push({
    index: true,
    element: target.element,
    HydrateFallback: target.HydrateFallback,
    action: target.action,
    loader: target.loader,
    handle: target.handle,
    errorElement: target.errorElement,
  });

  Object.assign(target, {
    element: layout.element,
    HydrateFallback: layout.HydrateFallback,
    action: layout.action,
    loader: layout.loader,
    handle: layout.handle,
    errorElement: layout.errorElement,
  });

  return target;
}

/**
 * Adds a route as index route under a target layout route.
 */
export function addRouteAsIndexRouteForTargetRoute(target, page) {
  target.children = target.children || [];
  target.children.push({
    index: true,
    element: page.element,
    HydrateFallback: page.HydrateFallback,
    action: page.action,
    loader: page.loader,
    handle: page.handle,
    errorElement: page.errorElement,
  });

  return target;
}

/**
 * Creates a new route configuration based on path segments and components.
 */
function createRoute(args) {
  const [current, ...rest] = args.segments;
  const [cleanPath, pageType] = current.split(separator);
  const route = { path: cleanPath };

  if (pageType === "page" || pageType === "layout") {
    route.element = <args.PageComponent />;
    route.HydrateFallback = args.LoadingComponent ?? (() => <div>Loading...</div>);
    route.action = args.action;
    route.loader = async (...props) => {
      if (!(await args.guard?.())) {
        throw new Response("Forbidden", { status: 403, statusText: "Forbidden" });
      }
      return args.loader?.(...props);
    };
    route.handle = { pageType };
  }

  if (rest.length > 0) {
    const nextSegment = rest[0].split(separator)[0];

    if (nextSegment === "update" || nextSegment === "edit") {
      return {
        path: `${cleanPath}/${nextSegment}`,
        element: <args.PageComponent />,
        HydrateFallback: args.LoadingComponent ?? (() => <div>Loading...</div>),
        action: args.action,
        loader: args.loader,
        handle: { pageType },
      };
    }

    const childRoute = createRoute({ ...args, segments: rest });

    if (!route.children) {
      route.children = [];
    }

    if (cleanPath.startsWith(":")) {
      route.children.unshift(childRoute);
    } else {
      route.children.push(childRoute);
    }
  }

  return route;
}

/**
 * Processes a file path to generate route segments.
 */
export function getRouteSegmentsFromFilePath(
  filePath,
  transformer = (segment, prevSegment) =>
    `${prevSegment}${separator}${getFileNameWithoutExtension(segment)}`,
) {
  const segments = filePath
    .replace("/app", "")
    .split("/")
    .filter((segment) => !segment.startsWith("(index)") && !segment.startsWith("_"))
    .map((segment) => {
      if (segment.startsWith(".")) return "/";
      if (segment.startsWith("("))
        return getParamFromSegment(segment).replace("(", "").replace(")", "") + "?";
      if (segment.startsWith("[")) return getParamFromSegment(segment);
      return segment;
    });

  return getRouteSegments(segments[0], segments, transformer);
}

function getFileNameWithoutExtension(file) {
  return file.split(".")[0];
}

function getRouteSegments(
  segment,
  segments,
  transformer,
  entries = [],
  index = 0,
) {
  if (index > segments.length) throw new Error("Cannot exceed total number of segments");

  if (index === segments.length - 1) {
    entries.push(transformer(segment, String(entries.pop())));
    return entries;
  }

  const nextIndex = index + 1;

  if (!segment.startsWith(":")) {
    entries.push(segment);
  } else {
    entries.push(`${entries.pop()}/${segment}`);
  }

  return getRouteSegments(segments[nextIndex], segments, transformer, entries, nextIndex);
}

function getParamFromSegment(segment) {
  if (segment.includes("...")) return "*";
  return segment.replace("[", ":").replace("]", "");
}

/**
 * Adds error boundaries to routes based on error component files.
 */
export function addErrorElementToRoutes(errorFiles, routes) {
  Object.entries(errorFiles).forEach(([filePath, importer]) => {
    const segments = getRouteSegmentsFromFilePath(filePath, (_, prevSegment) => prevSegment);
    const ErrorBoundary = lazy(importer);
    setRoute(segments, routes, (route) => {
      route.errorElement = <ErrorBoundary />;
      return route;
    });
  });
}

/**
 * Adds 404 (Not Found) pages to route children.
 */
export function add404PageToRoutesChildren(notFoundFiles, routes) {
  Object.entries(notFoundFiles).forEach(([filePath, importer]) => {
    const segments = getRouteSegmentsFromFilePath(filePath, (_, prevSegment) => prevSegment);
    const NotFound = lazy(importer);
    setRoute(segments, routes, (route) => {
      if (route.children) {
        set404NonPage(routes, <NotFound />);
        if (!route.children.some((child) => child.path === "*")) {
          route.children.push({ path: "*", element: <NotFound /> });
        }
      } else {
        const tempRoute = Object.assign({}, route);
        route.children = route.children ?? [];

        route.children.push({
          index: true,
          element: tempRoute.element,
          action: tempRoute.action,
          loader: tempRoute.loader,
        });

        route.children.push({ path: "*", element: <NotFound /> });

        delete route.element;
        delete route.action;
        delete route.loader;
      }
      return route;
    });
  });
}

function set404NonPage(routes, notFoundElement) {
  if (
    routes.path &&
    routes.children?.length &&
    !routes.path.includes("?") &&
    !routes.path.includes("/") &&
    !routes.children.some((child) => child.index)
  ) {
    routes.children.push({
      index: true,
      element: notFoundElement,
    });
  }
  routes.children?.forEach((route) => set404NonPage(route, notFoundElement));
}

/**
 * Recursively traverses and updates routes based on segment paths.
 */
function setRoute(segments, route, updater) {
  let temp = route;

  segments.forEach((_segment, i) => {
    const isLastSegment = i === segments.length - 1;
    if (isLastSegment) {
      temp = updater(temp);
      return;
    }

    const nextSegment = segments[i + 1];
    const index = temp.children?.findIndex((child) => child.path === nextSegment);

    if (typeof index !== "number" || index === -1) {
      const msg = `Segment ${nextSegment} does not exist among the children of route with path ${temp.path}`;
      throw new Error(msg);
    }

    temp = temp.children[index];
  });
}
